import { useEffect, useRef, useState } from 'react'
import { useNavigation } from 'react-router'
import { useSpinDelay } from 'spin-delay'
import { cn } from '#app/utils/misc.tsx'
import { Icon } from './ui/icon.tsx'

/**
 * Top-of-page navigation progress. ADR-025 (7.1): the bar animates `scale`
 * (transform, origin left) with ease-out instead of `width` with
 * ease-in-out; the wrapper pulses only while a navigation is actually
 * pending (and never under Reduce Motion); the announcement is Dutch; the
 * bar sits under the status bar in a Home-Screen install. The spinner is
 * guarded like StatusButton's (one Reduce Motion policy for both).
 */
function AppProgress() {
	const transition = useNavigation()
	const busy = transition.state !== 'idle'
	const delayedPending = useSpinDelay(busy, {
		delay: 600,
		minDuration: 400,
	})
	const ref = useRef<HTMLDivElement>(null)
	const [animationComplete, setAnimationComplete] = useState(true)

	useEffect(() => {
		if (!ref.current) return
		if (delayedPending) setAnimationComplete(false)

		const animationPromises = ref.current
			.getAnimations()
			.map(({ finished }) => finished)

		void Promise.allSettled(animationPromises).then(() => {
			if (!delayedPending) setAnimationComplete(true)
		})
	}, [delayedPending])

	return (
		<div
			role="progressbar"
			aria-hidden={delayedPending ? undefined : true}
			aria-valuetext={delayedPending ? 'Bezig met laden' : undefined}
			className={cn(
				'fixed inset-x-0 z-50 h-[0.2rem]',
				delayedPending && 'motion-safe:animate-pulse',
			)}
			style={{ top: 'env(safe-area-inset-top, 0px)' }}
		>
			<div
				ref={ref}
				className={cn(
					// Tailwind v4 scale-x-* writes the `scale` property, so the
					// transition list names `scale`, not `transform`
					'bg-foreground h-full w-full origin-left scale-x-0 transition-[scale,opacity] duration-500 ease-out motion-reduce:transition-none',
					transition.state === 'idle' &&
						(animationComplete ? 'transition-none' : 'scale-x-100 opacity-0'),
					delayedPending &&
						transition.state === 'submitting' &&
						'scale-x-[.42]',
					delayedPending && transition.state === 'loading' && 'scale-x-[.67]',
				)}
			/>
			{delayedPending && (
				<div className="absolute flex items-center justify-center">
					<Icon
						name="update"
						size="md"
						className="text-foreground m-1 animate-spin motion-reduce:animate-none"
						aria-hidden
					/>
				</div>
			)}
		</div>
	)
}

export { AppProgress }
